import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, Textarea } from './ui'
import Icon from './Icon'
import api from '../services/api'
import {
  AI_CHAT_STORAGE_EVENT,
  buildAiChatStorageKey,
  clearAiChatStorage,
  isAiChatPending,
  readAiChatLastError,
  readAiChatMessages,
  startAiChatSend,
} from '../services/aiChatSession'

const WAITING_MESSAGE_INTERVAL_MS = 10500
const WAITING_MESSAGES = [
  'Ща разберусь, не дёргайся ⚙️',
  'Секунду, ковыряюсь в данных 🧠',
  'Минутку, докопаюсь до сути 💪',
  'Работаю над твоим вопросом, не отходи далеко 👷‍♂️',
  'Проверяю всё, чтоб без кривды 🔍',
  'Кручу-верчу цифры, скоро будет результат 🔧',
  'Готовлю ответ, чтоб не стыдно было отправить 📐',
  'Обрабатываю запрос, потерпи малёк ⏳',
  'Роюсь в наших таблицах, ищу нужное 📊',
  'Ну да, я долго думаю, меня же девочки делали🙄',
  'Почти готово, сейчас выдам по-человечески ✅',
  'Не гони лошадей, сейчас всё достану 🐎',
  'Ща достану из недр системы то, что тебе надо 🕳️',
  'Дай пару секунд, уже черчу мысленно схему ✏️',
  'Проверяю, чтоб потом не переделывать 🙄',
  'Собираю всё в кучку, не разбегайся 🧩',
  'Грею процессор, обдумываю твой запрос 🔥',
  'Систему не пинаю, она сама думает 🤖',
  'Ща сверю цифры, чтоб не наляпать ошибок 📏',
  'Подожди, загляну в котёл с данными 🥘',
  'Выуживаю ответ из глубин таблицы 🎣',
  'Ща подожди, у нас тут не конвейер 🚧',
  'Дай вдохнуть, я же не робот… хотя стоп 🤔',
  'Копаюсь в архивах, пыль вековая летит 📚',
  'Мозг кипит, но ответ будет 🔩',
  'Ща всё сведу в нормальный вид 🧮',
  'Собираю информацию по крупицам 🪙',
  'Чутка терпения, расчёты не любят суеты 🧘‍♂️',
  'Заглянул в старые договоры, пыльно, но найду 📜',
  'Кручусь как инженер на планёрке, почти сделал 🔄',
  'Вытаскиваю данные из дебрей, подожди 🏕️',
]

/** Примеры запросов (концепт 3 / 3A) — подставляются в поле ввода */
export const AI_CHAT_SUGGESTIONS = [
  {
    text: 'Сколько Кочетов заработал за 2026 год?',
    hint: 'Доход сотрудника за год',
  },
  {
    text: 'Сколько заработала команда ИНЖ в 3м квартале 2026 года?',
    hint: 'Команда и квартал',
  },
  {
    text: 'Дай бюджет проекта Брестсткая 2?',
    hint: 'Финансы объекта',
  },
  {
    text: 'Покажи стадии договора ПР-2ПБ-22-25?',
    hint: 'Список стадий',
  },
]

export default function AiChatView({
  title = 'ИИ-ассистент',
  summary,
  promptHint,
  fullPage = false,
  onOpenFullPage,
  onClosePanel,
  user,
}) {
  const storageKey = useMemo(
    () => buildAiChatStorageKey(user?.id),
    [user?.id],
  )
  const [messages, setMessages] = useState(() => readAiChatMessages(storageKey))
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(() => isAiChatPending(storageKey))
  const [error, setError] = useState(() => readAiChatLastError(storageKey))
  const [waitingMessage, setWaitingMessage] = useState(WAITING_MESSAGES[0])
  const bodyRef = useRef(null)
  const formRef = useRef(null)
  const textareaRef = useRef(null)

  const syncFromStorage = useCallback(() => {
    setMessages(readAiChatMessages(storageKey))
    setSending(isAiChatPending(storageKey))
    setError(readAiChatLastError(storageKey))
  }, [storageKey])

  useEffect(() => {
    syncFromStorage()
  }, [storageKey, syncFromStorage])

  useEffect(() => {
    const handler = (event) => {
      if (event.detail?.storageKey !== storageKey) {
        return
      }
      syncFromStorage()
    }
    if (typeof window === 'undefined') {
      return undefined
    }
    window.addEventListener(AI_CHAT_STORAGE_EVENT, handler)
    return () => window.removeEventListener(AI_CHAT_STORAGE_EVENT, handler)
  }, [storageKey, syncFromStorage])

  useEffect(() => {
    const node = bodyRef.current
    if (node) {
      node.scrollTop = node.scrollHeight
    }
  }, [messages, sending])

  useEffect(() => {
    if (!sending) {
      return undefined
    }

    let index = Math.floor(Math.random() * WAITING_MESSAGES.length)
    setWaitingMessage(WAITING_MESSAGES[index])

    const intervalId = window.setInterval(() => {
      index = (index + 1 + Math.floor(Math.random() * 3)) % WAITING_MESSAGES.length
      setWaitingMessage(WAITING_MESSAGES[index])
    }, WAITING_MESSAGE_INTERVAL_MS)

    return () => window.clearInterval(intervalId)
  }, [sending])

  const clearHistory = () => {
    clearAiChatStorage(storageKey)
    setMessage('')
    syncFromStorage()
  }

  const applySuggestion = useCallback((text) => {
    setMessage(text)
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus()
    })
  }, [])

  const handleSubmit = (event) => {
    event.preventDefault()
    const trimmed = message.trim()
    if (!trimmed || sending || isAiChatPending(storageKey)) {
      return
    }

    setMessage('')
    setError('')
    startAiChatSend({
      storageKey,
      trimmedText: trimmed,
      client_context: promptHint || '',
      api,
    })
  }

  const handleComposerKeyDown = (event) => {
    if (event.key !== 'Enter') {
      return
    }
    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      return
    }
    event.preventDefault()
    if (!formRef.current) {
      return
    }
    if (formRef.current.requestSubmit) {
      formRef.current.requestSubmit()
    } else {
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
    }
  }

  const canSend = message.trim().length > 0 && !sending && !isAiChatPending(storageKey)
  const showSuggestions = messages.length === 0 && !sending

  const composerTextarea = (
    <Textarea
      ref={textareaRef}
      rows={fullPage ? 2 : 3}
      value={message}
      onChange={(event) => setMessage(event.target.value)}
      onKeyDown={handleComposerKeyDown}
      placeholder="Напишите сообщение боту..."
      disabled={sending}
      className="ai-chat-input"
    />
  )
  const shellClass = [
    'ai-chat-shell',
    fullPage ? 'ai-chat-shell--page ai-chat-shell--full-companion' : 'ai-chat-shell--sidebar-companion',
  ]
    .filter(Boolean)
    .join(' ')

  const renderSuggestionButtons = (layout) =>
    AI_CHAT_SUGGESTIONS.map((item) => (
      <button
        key={item.text}
        type="button"
        className={
          layout === 'grid'
            ? 'ai-chat-suggestion ai-chat-suggestion--grid'
            : 'ai-chat-suggestion ai-chat-suggestion--stack'
        }
        onClick={() => applySuggestion(item.text)}
      >
        {item.text}
        <small>{item.hint}</small>
      </button>
    ))

  return (
    <Card className={shellClass}>
      {!fullPage && (
        <div className="ai-chat-panel-header ai-chat-panel-header--sidebar-companion">
          <div className="ai-chat-sidebar-header-grid">
            {onClosePanel && (
              <button
                type="button"
                className="ai-chat-panel-icon-button"
                aria-label="Закрыть панель ассистента"
                onClick={onClosePanel}
              >
                <Icon name="close" size={18} />
              </button>
            )}
            {!onClosePanel && <span className="ai-chat-sidebar-header-spacer" aria-hidden="true" />}
            <div className="ai-chat-sidebar-header-center">
              <button
                type="button"
                className="ai-chat-link-ghost"
                onClick={clearHistory}
                disabled={sending}
              >
                Очистить чат
              </button>
              <div className="ai-chat-panel-title ai-chat-panel-title--sidebar">{title}</div>
            </div>
            {onOpenFullPage ? (
              <button
                type="button"
                className="ai-chat-panel-icon-button"
                aria-label="Открыть чат в полном режиме"
                onClick={onOpenFullPage}
              >
                <Icon name="expand" size={18} />
              </button>
            ) : (
              <span className="ai-chat-sidebar-header-spacer" aria-hidden="true" />
            )}
          </div>
        </div>
      )}

      {fullPage && (
        <div className="ai-chat-full-trust" role="status">
          <span className="ai-chat-trust-dot" aria-hidden="true" />
          <span>Тот же ответ, что в Telegram-боте 2ПБ</span>
        </div>
      )}

      <div
        ref={bodyRef}
        className={`ai-chat-body${fullPage ? ' ai-chat-body--full-companion' : ' ai-chat-body--sidebar-companion'}`}
      >
        {fullPage && messages.length > 0 && (
          <div className="ai-chat-full-active-bar">
            <span className="ai-chat-full-active-title">{title}</span>
          </div>
        )}

        {fullPage && showSuggestions && (
          <>
            <section className="ai-chat-full-intro" aria-label="О чате">
              <div className="ai-chat-avatar" aria-hidden="true">
                🤖
              </div>
              <h1 className="ai-chat-full-intro-title">ИИ-ассистент 2ПБ</h1>
              <p className="ai-chat-full-intro-text">
                Спросите про договоры, акты, согласования и цифры — я опираюсь на ваши данные в системе.
              </p>
              {summary ? (
                <div className="ai-chat-context-pill" title={promptHint}>
                  {summary}
                </div>
              ) : null}
            </section>
            <section className="ai-chat-suggestions-wrap" aria-label="Примеры запросов">
              <h2 className="ai-chat-suggestions-heading">Примеры запросов</h2>
              <div className="ai-chat-suggestions-grid">{renderSuggestionButtons('grid')}</div>
            </section>
          </>
        )}

        {!fullPage && showSuggestions && (
          <>
            <div className="ai-chat-sidebar-intro-row">
              <div className="ai-chat-avatar ai-chat-avatar--sm" aria-hidden="true">
                🤖
              </div>
              <div className="ai-chat-sidebar-intro-text">
                <h2 className="ai-chat-sidebar-intro-title">Чат с ботом 2ПБ</h2>
              </div>
            </div>
            <div className="ai-chat-trust-strip" role="status">
              <span className="ai-chat-trust-dot" aria-hidden="true" />
              <span>Тот же результат, что в Telegram-боте 2ПБ</span>
            </div>
            {summary ? (
              <div className="ai-chat-context-chip" title={promptHint}>
                <span>{summary}</span>
              </div>
            ) : null}
            <section className="ai-chat-suggestions-wrap ai-chat-suggestions-wrap--sidebar" aria-label="Примеры">
              <h2 className="ai-chat-suggestions-heading">Примеры</h2>
              <div className="ai-chat-suggestions-stack">{renderSuggestionButtons('stack')}</div>
            </section>
          </>
        )}

        {!showSuggestions && (
          <>
            {!fullPage && summary ? (
              <div className="ai-chat-context-chip ai-chat-context-chip--inline" title={promptHint}>
                <span>{summary}</span>
              </div>
            ) : null}
            {messages.map((item) => (
              <div
                key={item.id}
                className={`ai-chat-message ai-chat-message--${item.role}`}
              >
                <div className="ai-chat-message-label">
                  {item.role === 'user' ? 'Вы' : 'Бот'}
                </div>
                {item.html ? (
                  <div
                    className="ai-chat-message-bubble"
                    dangerouslySetInnerHTML={{ __html: item.html }}
                  />
                ) : (
                  <div className="ai-chat-message-bubble ai-chat-message-bubble--text">
                    {item.text}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
        {sending && (
          <div className="ai-chat-message ai-chat-message--assistant">
            <div className="ai-chat-message-label">Бот</div>
            <div className="ai-chat-message-bubble ai-chat-message-bubble--muted">
              <div className="ai-chat-loading">
                <span className="ai-chat-loading-text">{waitingMessage}</span>
                <span className="ai-chat-loading-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <form
        ref={formRef}
        className={`ai-chat-composer${fullPage ? ' ai-chat-composer--full-companion' : ' ai-chat-composer--sidebar-companion'}`}
        onSubmit={handleSubmit}
      >
        {error && <div className="ai-chat-error">{error}</div>}
        {!user?.employee_tg_id && (
          <div className="ai-chat-warning">
            Для чата нужен `Telegram ID` в карточке сотрудника.
          </div>
        )}
        <div
          className={
            fullPage
              ? 'ai-chat-composer-field-wrap ai-chat-composer-field-wrap--full'
              : 'ai-chat-composer-field-wrap ai-chat-composer-field-wrap--sidebar'
          }
        >
          {fullPage && (
            <button
              type="button"
              className="ai-chat-dock-icon-btn"
              disabled
              aria-label="Вложения (скоро)"
              title="Скоро"
            >
              <Icon name="plus" size={18} aria-hidden="true" alt="" />
            </button>
          )}
          {fullPage ? (
            <div className="ai-chat-input-shell">{composerTextarea}</div>
          ) : (
            composerTextarea
          )}
          {fullPage && (
            <Button
              type="submit"
              variant="primary"
              className="ai-chat-send-arrow"
              disabled={!canSend}
              aria-label={sending ? 'Отправка' : 'Отправить'}
            >
              <Icon name="send" size={20} aria-hidden="true" alt="" />
            </Button>
          )}
        </div>
        {fullPage ? (
          <div className="ai-chat-dock-meta">
            <span className="ai-chat-dock-meta-hint">
              Не передавайте пароли и персональные данные третьих лиц.
            </span>
            <button
              type="button"
              className="ai-chat-link-ghost ai-chat-link-ghost--footer"
              onClick={clearHistory}
              disabled={sending}
            >
              Очистить чат
            </button>
          </div>
        ) : (
          <div className="ai-chat-composer-actions ai-chat-composer-actions--sidebar">
            <span className="ai-chat-composer-hint">
              Без паролей и чужих персональных данных.
            </span>
            <Button type="submit" variant="primary" disabled={!canSend}>
              {sending ? 'Отправка...' : 'Отправить'}
            </Button>
          </div>
        )}
      </form>
    </Card>
  )
}
